import { useState } from "react";

import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { FixedNumber } from "ethers";
import BoxContainer from "~/../../base-components/lib/BoxContainer";
import {
  AlertSquare,
  CheckCircle,
} from "~/../../base-components/lib/assets/icons/HDS";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";
import { SECONDARY_COLOR } from "~/../../base-components/lib/theme/button";
import { maskAddress } from "~/../../shared-features/lib/utils/content";
import { getCurrencySymbol } from "~/../../shared-features/lib/utils/currency";

import { CollectDutchAuctionFundsModal } from "../modals/CollectDutchAuctionFundsModal/CollectDutchAuctionFundsModal";
import { GetManageTokenCollectionQuery } from "../queries.graphql.generated";

type DutchAuctionWithdrawFundsNoticeProps = {
  collection?: GetManageTokenCollectionQuery["getCollection"];
  mintVector?: GetManageTokenCollectionQuery["getCollection"]["mintVectors"][0];
};

export const DutchAuctionWithdrawFundsNotice = (
  props: DutchAuctionWithdrawFundsNoticeProps
) => {
  const { collection, mintVector } = props;
  const theme = useMantineTheme();
  const { isMedium } = useMantineMediaQueries();
  const [showModal, setShowModal] = useState(false);

  if (
    collection == null ||
    mintVector == null ||
    mintVector.id == null ||
    collection.editionId == null
  ) {
    return null;
  }

  const auctionStats = mintVector.stats?.onchainDutchAuctionStats;

  if (auctionStats == null) {
    return null;
  }

  const wereFundsWithdrawn = auctionStats.revenueWithdrawnFromEscrow;
  const handleWithdrawAuctionFunds = () => {
    if (wereFundsWithdrawn) return;
    setShowModal(true);
  };

  const fundsToClaim = auctionStats.fundsToClaim;
  const paymentRecipient = mintVector.paymentRecipient;
  const vectorId = mintVector.id;
  const col = {
    id: collection.id,
    collectionType: collection.collectionType,
    editionId: parseInt(collection.editionId),
  };

  const withdrawAmount = (() => {
    const totalSalesFN = FixedNumber.from(fundsToClaim);
    const symbol = getCurrencySymbol(collection.chainId);

    return `${totalSalesFN.toString()} ${symbol}`;
  })();

  const isExhausted = auctionStats.exhausted === true;

  return (
    <>
      <BoxContainer>
        {wereFundsWithdrawn ? (
          <Box>
            <Group>
              <CheckCircle color={theme.colors.successStatus[0]} />
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Text fw={500}>Funds withdrawn</Text>
                {isExhausted ? (
                  <Text size={"sm"}>
                    All funds were successfully transferred to{" "}
                    <Text span fw={500} inherit>
                      {maskAddress(mintVector.paymentRecipient)}
                    </Text>
                  </Text>
                ) : (
                  <Text size={"sm"}>
                    All funds from future mints will be automatically
                    transferred to{" "}
                    <Text span fw={500} inherit>
                      {maskAddress(mintVector.paymentRecipient)}
                    </Text>
                  </Text>
                )}
              </Stack>
            </Group>
          </Box>
        ) : (
          <Box>
            <Group>
              <AlertSquare color={theme.colors.informativeStatus[0]} />
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Text fw={500}>Withdrawing funds available</Text>
                <Text size={"sm"}>
                  The auction has reached its final resting price or has sold
                  out. You are now able to withdraw the funds.
                </Text>
              </Stack>
              <Button
                color={SECONDARY_COLOR}
                w={isMedium ? undefined : "100%"}
                onClick={handleWithdrawAuctionFunds}
              >
                <Text span inherit>
                  Withdraw{" "}
                  <Text inherit span>
                    {withdrawAmount}
                  </Text>
                </Text>
              </Button>
            </Group>
          </Box>
        )}
      </BoxContainer>

      <CollectDutchAuctionFundsModal
        isOpened={showModal}
        onClose={() => setShowModal(false)}
        fundsToClaim={withdrawAmount}
        paymentRecipient={paymentRecipient}
        vectorId={vectorId}
        collection={col}
      />
    </>
  );
};
